import {
  Box,
  ButtonBase,
  CircularProgress,
  styled,
  SxProps,
} from '@mui/material'
import { ReactNode } from 'react'
import { circularProgressClasses } from '@mui/material/CircularProgress'
import { CloseIcon, DownloadIcon } from '$common/icons'
import { BadgeWrapper } from '$common/badge/styled-badge'
import { nothing } from '$func/utils'

export type ActionHandlerState =
  | 'CONVERTING'
  | 'NOT_AVAILABLE' // show download icon
  | 'AVAILABLE' // show custom content
  | 'DOWNLOADING' // show circularProgress & cancelIcon
  | 'UPLOADING' // show circularProgress & cancelIcon

const Body = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  border-radius: 50%;
  position: relative;

  background-position: center;
  background-blend-mode: multiply;
  background-size: cover;
`

const StyledCircularProgress = styled(CircularProgress)`
  margin: auto;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  color: white;
  & .${circularProgressClasses.circle} {
    stroke-linecap: round;
  }
`

interface Props {
  state: ActionHandlerState
  percentage?: number
  coverUrl?: string
  size?: number
  rootSx?: SxProps
  sx?: SxProps

  customActionIconComp?: ReactNode
  customDownloadIconComp?: ReactNode
  onCustomActionIconClick?: () => unknown
  onCancelClick: () => unknown
  onDownloadClick: () => unknown
  selected?: boolean
  selectOnTap?: boolean
}

export function ActionHandler({
  state,
  percentage = 0,
  coverUrl,
  size = 50,
  rootSx,
  sx,
  customActionIconComp = null,
  onCustomActionIconClick = nothing,
  onCancelClick,
  onDownloadClick,
  selected = false,
  selectOnTap = false,
  customDownloadIconComp = null,
}: Props) {
  const isInProgress =
    state === 'UPLOADING' || state === 'DOWNLOADING' || state === 'CONVERTING'

  const action =
    selected || selectOnTap
      ? nothing
      : state === 'NOT_AVAILABLE'
      ? onDownloadClick
      : state === 'AVAILABLE'
      ? onCustomActionIconClick
      : isInProgress
      ? onCancelClick
      : nothing

  return (
    <Box sx={rootSx}>
      <BadgeWrapper selected={selected}>
        <Body
          sx={{
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: coverUrl ? '#0000007F' : '#55A6FF',
            backgroundImage: coverUrl ? `url("${coverUrl}")` : 'unset',
            cursor: selected || selectOnTap ? 'default' : 'pointer',
            ...sx,
          }}
          onClick={action}
        >
          {isInProgress && (
            <StyledCircularProgress
              variant="determinate"
              value={percentage}
              size={size - 4}
              thickness={2.5}
              disableShrink
            />
          )}

          {state === 'AVAILABLE' ? (
            customActionIconComp
          ) : state === 'NOT_AVAILABLE' ? (
            customDownloadIconComp || (
              <DownloadIcon sx={{ color: 'neutral000' }} />
            )
          ) : (
            <CloseIcon
              sx={{ color: 'neutral000', width: '24px', height: '24px' }}
            />
          )}
        </Body>
      </BadgeWrapper>
    </Box>
  )
}
